import React, { useMemo, useState } from 'react'
import { animated } from 'react-spring'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'
import { useDropDown } from '~/hooks/dropdown'

import Organization from '~/models/organization'
import Tool, { ToolTypeModel } from '~/models/tool'
import {
  ORGANIZATION_TOOLS, OrganizationToolListResponse,
  OrganizationToolSearchListVars,
} from '~/api/tool'
import { partialMatch } from '~/api/utils'
import { ORGANIZATION_FILES, OrganizationFileListResponse, OrganizationFileListVars } from '~/api/file'

import { SEARCH_LIST_PAGINATION } from '~/common/utils/pagination'
import { sortModelsWithUpdatedAt } from '~/common/utils/sort'
import TabSwitch from '~/components/atoms/tab/TabSwitch'
import Loading from '~/components/atoms/loading/Loading'
import ToolFileList from '~/components/app/modules/tool/list/ToolFileList'
import ToolList from '~/components/app/modules/tool/list/ToolList'
import FileList from '~/components/app/modules/file/FileList'
import { TextBoxSize } from '~/components/atoms/textbox/TextBox'
import { IconColor } from '~/components/atoms/icon/Icon'
import SearchIconTextBox from '~/components/molecules/textbox/SearchIconTextBox'
import { RowBaseLayoutSize } from '~/components/molecules/row/content/RowBaseLayout'
import { searchPath } from '~/common/define/path'

interface Props {
  organization: Organization
  alwaysActive: boolean
}

const SearchDropdown = ({organization, alwaysActive}: Props) => {
  const {t} = useTranslation()
  const router = useRouter()
  const [text, setText] = useState<string>("")
  const {clickableRef, onClickOpen, dropDownStyles, buttonClass, dropDownClass, open} = useDropDown()
  const pagination = SEARCH_LIST_PAGINATION

  const {
    loading: toolLoading,
    data: toolData
  } = useQuery<OrganizationToolListResponse, OrganizationToolSearchListVars>(ORGANIZATION_TOOLS,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        organization_id: organization.id,
        text: text,
        first: pagination.perPage,
        page: pagination.currentPage,
      },
    },
  )

  const {
    loading: fileLoading,
    data: fileData
  } = useQuery<OrganizationFileListResponse, OrganizationFileListVars>(ORGANIZATION_FILES,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        organization_id: organization.id,
        first: pagination.perPage,
        page: pagination.currentPage,
        text: partialMatch(text)
      },
    },
  )

  const tools: Tool[] = useMemo(() => {
    return toolData ? toolData.organizationTools.data : []
  }, [toolData])

  const wikis = useMemo(() => sortModelsWithUpdatedAt(tools.filter((tool: Tool) => {
    return tool.type == ToolTypeModel.WIKI
  })), [tools])

  const otherTools = useMemo(() => sortModelsWithUpdatedAt(tools.filter((tool: Tool) => {
    return tool.type != ToolTypeModel.WIKI
  })), [tools])

  const files = useMemo(() => sortModelsWithUpdatedAt((fileData ? fileData.organizationFiles.data : [])
  ), [fileData])

  const handlePush = () => {
    (open || alwaysActive) ? router.push(searchPath(organization, text)) : onClickOpen()
  }

  return (
    <div onClick={onClickOpen} ref={clickableRef} className={buttonClass}>
      <SearchIconTextBox size={TextBoxSize.MIDDLE} onChangeText={setText} handleSubmit={handlePush} color={IconColor.WHITE}
                         alwaysActive={alwaysActive} close={!alwaysActive && !open} toolTipText={t('header:tooltip.search')} placeholder={t('header:all_search')}/>
      {dropDownStyles &&
        <animated.div style={dropDownStyles} className={dropDownClass}>
          <TabSwitch
            items={[
              {text: t('header:search.all')},
              {text: t('header:search.wiki', {'quantity': wikis.length})},
              {text: t('header:search.file', {'quantity': files.length})},
              {text: t('header:search.tools', {'quantity': otherTools.length})},
            ]}
            contents={
              (fileLoading || toolLoading) ?
                [<Loading key={1}/>] :
                [
                  <ToolFileList key={1} tools={tools} files={files} size={RowBaseLayoutSize.SMALL}/>,
                  <ToolList key={2} tools={wikis} size={RowBaseLayoutSize.SMALL}/>,
                  <FileList key={3} files={files} size={RowBaseLayoutSize.SMALL}/>,
                  <ToolList key={4} tools={otherTools} size={RowBaseLayoutSize.SMALL}/>,
                ]
            }
          />
        </animated.div>
      }
    </div>
  )
}

export default SearchDropdown
